import { ChevronDown } from 'lucide-react';
import { forwardRef, useId, type AriaAttributes } from 'react';

import { triggerStyle, type RegionTreeVariant } from './region-tree-styles';

interface RegionTreeTriggerProps extends Pick<
  AriaAttributes,
  'aria-labelledby' | 'aria-describedby' | 'aria-required' | 'aria-invalid'
> {
  open: boolean;
  /** Text shown, or null to show the placeholder. */
  label: string | null;
  placeholder: string;
  variant: RegionTreeVariant;
  onToggle: () => void;
}

const DEFAULT_TRIGGER =
  'flex items-center justify-between gap-2 w-full px-3 py-1.5 text-sm rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-left';

/** The button that opens the region tree. */
const RegionTreeTrigger = forwardRef<HTMLButtonElement, RegionTreeTriggerProps>(
  function RegionTreeTrigger(props, ref) {
    const { open, label, placeholder, variant, onToggle } = props;
    const labelledBy = props['aria-labelledby'];
    const valueId = useId();
    const tokens = variant === 'dashboards';
    return (
      <button
        ref={ref}
        type="button"
        aria-haspopup="tree"
        aria-expanded={open}
        aria-labelledby={labelledBy ? `${labelledBy} ${valueId}` : undefined}
        aria-describedby={props['aria-describedby']}
        aria-required={props['aria-required']}
        aria-invalid={props['aria-invalid']}
        onClick={onToggle}
        className={tokens ? undefined : DEFAULT_TRIGGER}
        style={triggerStyle(variant, Boolean(props['aria-invalid']))}
      >
        <span
          id={valueId}
          className={
            label ? 'text-bark-900 dark:text-stone-100' : 'text-stone-400 dark:text-stone-500'
          }
          style={tokens && !label ? { color: 'var(--nb-subtle)' } : undefined}
        >
          {label ?? placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`flex-shrink-0 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
    );
  },
);

export default RegionTreeTrigger;
